<template>
  <LcLotteryEditorCard
    ref="card"
    v-bind="{ complete, remaining, fields }"
    v-model:expanded="isExpanded"
    :[EDITOR_PLAY_ID_ATTRIBUTE]="play.id"
    :clear-disabled="!clearEnabled"
    :done-label="$t('numberlottery.remainingnumbers.done')"
    :quick-pick-label="$t('numberlottery.playslip.button.quickpick')"
    :clear-label="$t('numberlottery.playslip.button.clear')"
    :number-class="numberClass"
    :warning="warning"
    :removable="isRemovable"
    :remove-aria-label="$t('editor.card.remove.arialabel')"
    @clear="handleClear"
    @remove="closePlay(play.id)"
    @quick-pick="handleQuickPick"
    @collapsed="notifyCollapsed(play.id)"
    @expanded="scrollExpandedPlayIntoView(cardRef)"
    @update:expanded="handleExpandedChange"
    separated
  >
    <template #headline="{ countClass }">
      <EditorRemainingHeadline
        v-bind="{ countClass }"
        keypath="numberlottery.remainingnumbers"
        :name="$t('numberlottery.remainingnumbers.name.base', visualRemaining)"
        :remaining="visualRemaining"
      />
    </template>

    <section class="flex flex-col gap-100 p-200 rounded-m bg-secondary-secondary">
      <div class="row flex justify-center w-full">
        <span
          v-for="{ name } in config.numbers"
          :key="name"
          class="column paragraph-bold text-secondary-secondary text-center"
        >
          {{ $t(`lottery.nebraskamyday.bettype.${DATE_PART_BY_FIELD_NAME[name]}`) }}
        </span>
      </div>

      <ul
        class="row wheels flex items-center justify-center w-full bg-primary-primary py-200 rounded-m"
      >
        <li v-for="{ name } in config.numbers" :key="name" class="column">
          <LcSlotWheel
            :options="optionsByFieldName[name]"
            :value="values[name] ?? null"
            @options-changed="() => handleOptionsChanged(name)"
            @re-init="() => handleReInit(name)"
            @update:value="(value) => handleUpdate(name, value)"
          >
            <template #option="{ option }">
              <span :aria-disabled="option.value === null" :aria-label="ariaLabelOf(option, name)">
                {{ option.label }}
              </span>
            </template>
          </LcSlotWheel>
        </li>
      </ul>

      <p>{{ $t('lottery.nebraskamydaylottery.playslip.selector.message') }}</p>
    </section>
  </LcLotteryEditorCard>
</template>

<script lang="ts" setup>
import type { LcLotteryNumbersProps, LcSlotWheelOption } from '@lottocom/frontend-components'
import type { ComponentPublicInstance } from 'vue'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { DATE_LOTTERY_FIELD_NAME, DATE_PART_BY_FIELD_NAME } from '../constants/dateLottery'
import { EDITOR_PLAY_ID_ATTRIBUTE } from '../constants/editor'
import { EDITOR_QUICK_PICK_MOTION } from '../constants/quickPickAnimation'
import { type EditorPlay, useDateLotteryStore } from '../stores/dateLottery'
import { clampDay, formatDatePartValue, getDaysInMonth } from '../utils/editorDate'
import { buildCardFields, findFieldIndexByName } from '../utils/editorFields'

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
}

const { config, lotteryId, play } = defineProps<Props>()

const { t } = useI18n()

const store = useDateLotteryStore(lotteryId)
const closePlay = useEditorClosePlay(store)

const { scrollExpandedPlayIntoView, scrollExpandingPlayIntoView } = useExpandableScroll()
const { cancel, getNumberState, getSummaryPlay, notifyCollapsed } = useEditorQuickPickAnimation()
const cardRef = useTemplateRef<ComponentPublicInstance>('card')

const toOptions = (numbers: number[], name: string) => [
  { label: '-', value: null },
  ...numbers.map((value) => ({ label: formatDatePartValue(name, value), value })),
]

const clearEnabled = computed(() => play.fields.some(({ selected }) => selected.length > 0))

const complete = computed(() => store.isPlayComplete(config, play))

const fields = computed(() =>
  buildCardFields(config, getSummaryPlay(play), () => ({})).map((field) =>
    field.name === DATE_LOTTERY_FIELD_NAME.Year
      ? {
          ...field,
          numbers: field.numbers.map((number) => ({
            ...number,
            value:
              number.value === undefined
                ? number.value
                : formatDatePartValue(field.name, number.value),
          })),
        }
      : field,
  ),
)

const isExpanded = computed({
  get: () => store.expandedPlayId === play.id,
  set: (expanded) => store.setExpandedPlay(expanded ? play.id : null),
})

const warning = computed(() =>
  !isExpanded.value && store.isPlayFlagged(config, play)
    ? t('editor.play.badge.incomplete')
    : undefined,
)

const isRemovable = computed(
  () => isExpanded.value && store.isPlayEmpty(play) && store.plays.length > 1,
)

const selectedMonth = computed(() => valueOf(DATE_LOTTERY_FIELD_NAME.Month))

const selectedYear = computed(() => valueOf(DATE_LOTTERY_FIELD_NAME.Year))

const selectableNumbersOf = ({
  name,
  selectableNumbers,
}: {
  name: string
  selectableNumbers: number[]
}) => {
  if (name !== DATE_LOTTERY_FIELD_NAME.Day) return selectableNumbers

  const month = selectedMonth.value
  if (month === null) return selectableNumbers

  const daysInMonth = getDaysInMonth(month, selectedYear.value)
  return selectableNumbers.filter((day) => day <= daysInMonth)
}

const optionsByFieldName = computed(() =>
  Object.fromEntries(
    config.numbers.map((number) => [
      number.name,
      toOptions(selectableNumbersOf(number), number.name),
    ]),
  ),
)

const remaining = computed(() => play.fields.filter(({ selected }) => !selected.length).length)

const visualRemaining = computed(() => remaining.value || 1)

const numberClass: LcLotteryNumbersProps['numberClass'] = (value, name) => {
  const state = getNumberState(play.id, typeof value === 'string' ? Number(value) : value, name)
  return state ? `date-lottery-ball-${state}` : ''
}

const ariaLabelOf = (option: LcSlotWheelOption, name: string) =>
  option.value === null
    ? t('lottery.nebraskamyday.scrollwheel.arialabel.ariadisabled', {
        value: DATE_PART_BY_FIELD_NAME[name],
      })
    : t('lottery.nebraskamyday.scrollwheel.arialabel', {
        option: option.value,
        value: DATE_PART_BY_FIELD_NAME[name],
      })

const valueOf = (name: string) => {
  const index = findFieldIndexByName(play, name)
  return index < 0 ? null : (play.fields[index].selected[0] ?? null)
}

const isReinitializing = ref(false)

const values = ref<Record<string, Maybe<number>>>(
  Object.fromEntries(config.numbers.map(({ name }) => [name, valueOf(name)])),
)

const handleOptionsChanged = (name: string) => {
  if (name !== DATE_LOTTERY_FIELD_NAME.Day) return
  isReinitializing.value = true
}

const handleReInit = async (name: string) => {
  if (name !== DATE_LOTTERY_FIELD_NAME.Day) return
  await nextTick()
  isReinitializing.value = false
}

watchDeep(
  () => play.fields,
  async () => {
    await until(isReinitializing).toBe(false)
    config.numbers.forEach(({ name }) => (values.value[name] = valueOf(name)))
  },
)

const handleClear = () => {
  if (!clearEnabled.value) return
  store.clearPlay(play.id)
}

const handleExpandedChange = (expanded: boolean) => {
  cancel(play.id)
  if (!expanded) return
  scrollExpandingPlayIntoView(cardRef.value)
}

const handleQuickPick = () => {
  store.quickPick(play.id, config)
}

const handleUpdate = (name: string, value: LcSlotWheelOption['value']) => {
  const index = findFieldIndexByName(play, name)
  if (index < 0 || valueOf(name) === value) return

  store.setSelectedNumbers(play.id, index, value === null ? [] : [value])
}

watch([selectedMonth, selectedYear], () => {
  const day = valueOf(DATE_LOTTERY_FIELD_NAME.Day)
  const month = selectedMonth.value
  if (day === null || month === null) return

  const clamped = clampDay(day, month, selectedYear.value)
  if (clamped === day) return

  handleUpdate(DATE_LOTTERY_FIELD_NAME.Day, clamped)
})

const quickPickMotion = EDITOR_QUICK_PICK_MOTION
</script>

<style lang="scss" scoped>
$wheel-active-element-size: 3.5625rem;
$roll-tick: v-bind("quickPickMotion.tick + 'ms'");
$settle-duration: v-bind("quickPickMotion.settle + 'ms'");

.row {
  gap: token('size/space/gap/500');

  @include media-desktop {
    gap: token('size/space/gap/950');
  }
}

.column {
  flex: none;
  width: $wheel-active-element-size;
}

.wheels {
  border: token('size/border/100') solid token('color/border/primary/primary');
}

:deep(.date-lottery-ball-rolling) {
  overflow: hidden;

  /* stylelint-disable-next-line selector-class-pattern */
  .lc-lottery-numbers__value {
    animation: date-lottery-ball-roll $roll-tick linear infinite;
  }
}

:deep(.date-lottery-ball-locked) {
  animation: date-lottery-ball-settle $settle-duration $transition-ease-pop;
}

@keyframes date-lottery-ball-roll {
  from {
    opacity: 0.35;
    transform: translateY(-55%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes date-lottery-ball-settle {
  from {
    transform: scale(1.07);
  }

  to {
    transform: scale(1);
  }
}
</style>
